import {t, TFunction} from 'i18next';
import {
  fetchSubtallyReport,
  fetchSubtallyResult,
  fetchTallyReport,
  fetchTallyResult,
  getDocReport,
  getExportView,
  getReportDocId,
  getSubTallyExportReport,
  getTallyExportReport,
} from '../api/endpoints/reportingApi';
import {
  IGetDocReportResponse,
  IGetExportViewResponse,
  IGetReportDocIdResponse,
  ISubtallyDataResponseBody,
  ISubtallyResultResponseBody,
  ITallyDataResponseBody,
  ITallyResultResponseBody,
  SubTallyExportResponseBody,
  TallyExportResponseBody,
} from '../api/types/reportingApi.type';
import {ActiveFilter} from '../components/common/arr-filter-panel/filter-accordion/ArrFilterAccordion';
import {TableRowData} from '../components/reports/standard-reports/config/reportTypes';
import {ReportActionMenuOptionTypes, RoleId} from '../shared/constants';
import {DocActionMenuItem} from '../shared/interfaces';
import {TagNode} from '../shared/types';
import {SelectionItem} from '../components/reports/tally/selection-by/SelectionBy';
type FetchTallyReport = typeof fetchTallyReport;
type FetchTallyResult = typeof fetchTallyResult;
type FetchSubtallyReport = typeof fetchSubtallyReport;
type FetchSubtallyResult = typeof fetchSubtallyResult;
type GetTallyExportReport = typeof getTallyExportReport;
type GetSubTallyExportReport = typeof getSubTallyExportReport;
type GetReportDocId = typeof getReportDocId;
type GetExportView = typeof getExportView;
type GetDocReport = typeof getDocReport;

interface TagConfig {
  idKey: string;
  textKey: string;
  unqKey: string;
}

export const buildtagHeirarchy = (
  tags: any[],
  config: TagConfig
): TagNode[] => {
  const {idKey, textKey, unqKey} = config;
  const map: Record<string, TagNode> = {};
  const roots: TagNode[] = [];

  tags.forEach((tag) => {
    if (Object.hasOwn(tag, idKey) && Object.hasOwn(tag, textKey)) {
      const idValue = tag[idKey];
      const textValue = tag[textKey];
      map[tag[idKey]] = {
        id: String(unqKey + idValue),
        label: String(textValue),
        children: [],
      };
    }
  });

  tags.forEach((tag) => {
    const node = map[tag[idKey]];

    if (tag.parent && tag.parent !== '#' && map[tag.parent]) {
      map[tag.parent].children!.push(node);
    } else {
      roots.push(node);
    }
  });

  const removeEmptyChildren = (nodes: TagNode[]): TagNode[] =>
    nodes.map((node) => {
      const cleanNode: TagNode = {id: node.id, label: node.label};
      if (node.children && node.children.length > 0) {
        cleanNode.children = removeEmptyChildren(node.children);
      }
      return cleanNode;
    });

  return removeEmptyChildren(roots);
};

const cleanedlabelIds = (data: any) => {
  return data
    .map((item: any) => item.id)
    .filter(Boolean)
    .join(',');
};

export const handleApplyUtil = async ({
  selectedSource,
  treeSelect,
  confirmedData,
  applyType,
  selectedInfo,
  confirmedFilters,
  fetchTallyReport,
  fetchSubtallyReport,
  tallyByCriteriaList,
}: {
  selectedSource: any;
  treeSelect: any[];
  confirmedData: any;
  applyType: 'tally' | 'subtally';
  selectedInfo: any;
  confirmedFilters: any;
  fetchTallyReport: FetchTallyReport;
  fetchSubtallyReport: FetchSubtallyReport;
  tallyByCriteriaList: any;
}): Promise<{
  success: boolean;
  data?: ITallyDataResponseBody | ISubtallyDataResponseBody;
}> => {
  try {
    const reportResponse =
      applyType === 'tally'
        ? await fetchTallyReport({
            sourceName: selectedSource.sourceName,
            source: selectedSource.source,
            tallyById: cleanedIds(treeSelect),
            tallyByText: confirmedData.id,
            filters: transformFilters(confirmedFilters),
            tallyFieldType: '',
            navigationSourceType: selectedSource.navigationSourceType,
            tallyData: {
              lstSecurityGroups: [],
              lstMetadata: [],
              lstProjects: [],
              lstTagsList: [],
              lstDocuments: [],
              folderTreeList: [],
              SearchTreeList: [],
              aiScoreTreeList: [],
              lstFileSizeType: [],
              assignmentTreeList: [],
              lstPopupSqlMetaData: [],
              lstFilterSqlMetaData: [],
              lstProductionMetadata: [],
              lstRedactionTagsList: [],
              lstTagClassification: [],
              xaxis: '',
              yaxis: '',
            },
          })
        : await fetchSubtallyReport({
            tallyByText: selectedInfo?.tallyName,
            tallyById:
              selectedInfo?.tallyName == 'Metadata'
                ? cleanedIds(selectedInfo?.tallyId)
                : cleanedlabelIds(selectedInfo?.barResult),
            subTallyById: cleanedIds(treeSelect),
            subTallyByText: confirmedData.id,
            tallySelection: selectedInfo?.barResult?.map(
              (item: any) => item.label
            ),
            filters: transformFilters(confirmedFilters),
            subtallyByType: '',
            tallyFilter: {
              sourceType: selectedSource.sourceName,
              sourceID: selectedSource.source,
              docListID: 0,
              tally: {
                tallyByID:
                  selectedInfo?.tallyName == 'Metadata'
                    ? cleanedIds(selectedInfo?.tallyId)
                    : '',
                tallyBy: null,
                docListID: 0,
                tallyByCriteriaList: tallyByCriteriaList,
                filterByMetaData: selectedInfo?.tallyFilters
                  ? transformMDFilters(selectedInfo.tallyFilters)
                  : [],
                filterBySelection: [],
                isBulkAction: false,
                tallyByType: selectedInfo?.tallyName,
                tallyByIDExceptMetadata:
                  selectedInfo?.tallyName == 'Metadata'
                    ? null
                    : cleanedIds(selectedInfo?.tallyId),
                subTallyByID: null,
                subTallyByIDExceptMetadata: null,
                subTallyByType: null,
                tallyResultIDExceptMetadata:
                  selectedInfo?.tallyName == 'Metadata'
                    ? null
                    : cleanedlabelIds(selectedInfo?.barResult),
                tallyResultID: null,
                subTallyByResultIDExceptMetadata: null,
                tallyFilterValue: [],
                subTallyFilterValue: [],
              },
              subTally: {
                tallyByID: cleanedIds(treeSelect),
                tallyBy: null,
                docListID: 0,
                tallyByCriteriaList: [],
                filterByMetaData: [],
                filterBySelection: [],
                isBulkAction: false,
                tallyByType: confirmedData.id,
                tallyByIDExceptMetadata: null,
                subTallyByID: null,
                subTallyByIDExceptMetadata: null,
                subTallyByType: null,
                tallyResultIDExceptMetadata: null,
                tallyResultID: null,
                subTallyByResultIDExceptMetadata: null,
                tallyFilterValue: [],
                subTallyFilterValue: [],
              },
              dataAsOf: new Date().toISOString(),
              isDocumentCount: false,
              documentCount: 0,
              navigationSourceType: selectedSource.navigationSourceType,
              reportFilePath: null,
              reportName: null,
            },
          });
    return {success: true, data: reportResponse};
  } catch {
    return {success: false};
  }
};

export const handleResultUtil = async ({
  applyType,
  fetchTallyResult,
  fetchSubtallyResult,
  reportResponse,
}: {
  applyType: string;
  fetchTallyResult: FetchTallyResult;
  fetchSubtallyResult: FetchSubtallyResult;
  reportResponse: ITallyDataResponseBody | ISubtallyDataResponseBody;
}): Promise<{
  success: boolean;
  data?: ITallyResultResponseBody | ISubtallyResultResponseBody;
}> => {
  const dataAsOf =
    applyType === 'tally' && 'dataAsOf' in reportResponse.tallyFilter
      ? (reportResponse.tallyFilter?.dataAsOf ?? '')
      : 'shareReportData' in reportResponse
        ? (reportResponse.shareReportData?.dataAsOf ?? '')
        : '';

  const reportFilePath =
    applyType === 'tally' && 'backgroundModelData' in reportResponse
      ? cleanedPath(reportResponse.backgroundModelData?.reportFilePath ?? '')
      : cleanedPath(reportResponse.tallyFilter?.reportFilePath ?? '');

  const reportName =
    applyType === 'tally' && 'backgroundModelData' in reportResponse
      ? (reportResponse.backgroundModelData?.reportName ?? '')
      : (reportResponse.tallyFilter?.reportName ?? '');

  const makeBaseModel = (data: any) => {
    const tallyfilters = data?.tallyFilter;

    if (tallyfilters) {
      const CapTallyFilters = capitalizeKeys(tallyfilters);
      CapTallyFilters.ReportFilePath = data?.shareReportData.reportFilePath;
      CapTallyFilters.ReportName = data?.shareReportData.reportName;
      return JSON.stringify(CapTallyFilters);
    } else {
      return '';
    }
  };

  const capitalizeKeys = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(capitalizeKeys);
    } else if (obj && typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key.charAt(0).toUpperCase() + key.slice(1),
          capitalizeKeys(value),
        ])
      );
    }
    return obj;
  };

  try {
    const resultResponse =
      applyType === 'tally'
        ? await fetchTallyResult({
            sourceID: reportResponse?.tallyFilter.sourceID ?? '',
            sourceType: reportResponse?.tallyFilter.sourceType ?? '',
            docListID: 0,
            tally: sanitizedPayload(reportResponse?.tallyFilter?.tally ?? []),
            subTally: sanitizedPayload(
              reportResponse?.tallyFilter?.subTally ?? []
            ),
            dataAsOf,
            isDocumentCount:
              reportResponse?.tallyFilter?.isDocumentCount ?? false,
            documentCount: reportResponse?.tallyFilter?.documentCount ?? 0,
            navigationSourceType:
              reportResponse?.tallyFilter?.navigationSourceType ?? '',
            reportFilePath,
            reportName,
          })
        : await fetchSubtallyResult({
            baseModel: makeBaseModel(reportResponse),
            tallyByText: reportResponse?.tallyFilter?.tally.tallyByType ?? '',
            subTallyByText:
              reportResponse?.tallyFilter?.subTally.subTallyByType ?? '',
            tallyInput: {
              lstSecurityGroups: [],
              lstProjects: [],
              lstMetadata: [],
              lstProductionMetadata: [],
              lstDocuments: [],
              searchTreeList: [],
              assignmentTreeList: [],
              folderTreeList: [],
              lstPopupSqlMetaData: [],
              lstFilterSqlMetaData: [],
              lstFileSizeType: [],
              lstTagsList: [],
              lstRedactionTagsList: [],
              lstTagClassification: [],
              aiScoreTreeList: [],
              xaxis: '',
              yaxis: '',
              isDocCountView: false,
              divisorFromBytes: 0,
            },
            tallyFilter: {
              sourceType: reportResponse?.tallyFilter.sourceType ?? '',
              sourceID: reportResponse?.tallyFilter.sourceID ?? '',
              docListID: 0,
              tally: reportResponse?.tallyFilter.tally,
              subTally: reportResponse.tallyFilter.subTally,
              dataAsOf: new Date().toISOString(),
              isDocumentCount: true,
              documentCount: 0,
              navigationSourceType:
                reportResponse.tallyFilter.navigationSourceType,
              reportFilePath: '',
              reportName: '',
            },
          });
    return {success: true, data: resultResponse};
  } catch {
    return {success: false};
  }
};

export const handleAutoResultUtil = async ({
  fetchSubtallyResult,
  preSelects,
}: {
  fetchSubtallyResult: FetchSubtallyResult;
  preSelects: any;
}): Promise<{
  success: boolean;
  data?: ISubtallyResultResponseBody;
}> => {
  const makeBaseModel = (data: any) => {
    const tallyfilters = data?.tallyFilter;

    if (tallyfilters) {
      const CapTallyFilters = capitalizeKeys(tallyfilters);
      CapTallyFilters.ReportFilePath = tallyfilters.reportFilePath;
      CapTallyFilters.ReportName = tallyfilters.reportName;
      return JSON.stringify(CapTallyFilters);
    } else {
      return '';
    }
  };

  const capitalizeKeys = (obj: any): any => {
    if (Array.isArray(obj)) {
      return obj.map(capitalizeKeys);
    } else if (obj && typeof obj === 'object') {
      return Object.fromEntries(
        Object.entries(obj).map(([key, value]) => [
          key.charAt(0).toUpperCase() + key.slice(1),
          capitalizeKeys(value),
        ])
      );
    }
    return obj;
  };

  try {
    const resultResponse = await fetchSubtallyResult({
      baseModel: makeBaseModel(preSelects),
      tallyByText: preSelects?.tallyFilter?.tally.tallyByType ?? '',
      subTallyByText: preSelects?.tallyFilter?.subTally.subTallyByType ?? '',
      tallyInput: {
        lstSecurityGroups: [],
        lstProjects: [],
        lstMetadata: [],
        lstProductionMetadata: [],
        lstDocuments: [],
        searchTreeList: [],
        assignmentTreeList: [],
        folderTreeList: [],
        lstPopupSqlMetaData: [],
        lstFilterSqlMetaData: [],
        lstFileSizeType: [],
        lstTagsList: [],
        lstRedactionTagsList: [],
        lstTagClassification: [],
        aiScoreTreeList: [],
        xaxis: '',
        yaxis: '',
        isDocCountView: false,
        divisorFromBytes: 0,
      },
      tallyFilter: {
        sourceType: preSelects?.tallyFilter.sourceType ?? '',
        sourceID: preSelects?.tallyFilter.sourceID ?? '',
        docListID: 0,
        tally: preSelects?.tallyFilter.tally,
        subTally: preSelects?.tallyFilter.subTally,
        dataAsOf: preSelects?.tallyFilter?.dataAsOf,
        isDocumentCount: true,
        documentCount: 0,
        navigationSourceType: preSelects?.tallyFilter?.navigationSourceType,
        reportFilePath: preSelects?.tallyFilter?.reportFilePath ?? '',
        reportName: preSelects?.tallyFilter?.reportName ?? '',
      },
    });
    return {success: true, data: resultResponse};
  } catch {
    return {success: false};
  }
};

export const handleExportUtil = async ({
  tallyResponse,
  sourceData,
  tallySelectType,
  selectedSource,
  filterTallySelectionsView,
  dateTime,
  getTallyExportReport,
}: {
  tallyResponse: any;
  sourceData: any;
  tallySelectType: any;
  selectedSource: any;
  filterTallySelectionsView: any;
  getTallyExportReport: GetTallyExportReport;
  dateTime: string;
}): Promise<{
  success: boolean;
  data?: TallyExportResponseBody;
  error?: any;
}> => {
  try {
    const exportResponse = await getTallyExportReport({
      sourceData: sourceData,
      filterTallySelectionsView: filterTallySelectionsView,
      selectSourceView: selectedSource[0].labels,
      tallyselecttype: tallySelectType,
      actionType: 'EXPORTREPORT',
      reportType: tallyResponse.reportType ?? '',
      dataAsOf: dateTime ? dateTime : '',
    });
    return {success: true, data: exportResponse};
  } catch (error) {
    return {success: false, error: error};
  }
};

export const handleExportSubtallyUtil = async ({
  subTallyResponse,
  selectedSource,
  sourceType,
  filterTallySelectionsView,
  subTallyBy,
  tallySelectType,
  sourceData,
  dateTime,
  getSubTallyExportReport,
}: {
  subTallyResponse: any;
  selectedSource: any;
  sourceType: any;
  filterTallySelectionsView: any;
  subTallyBy: any;
  tallySelectType: any;
  sourceData: any;
  getSubTallyExportReport: GetSubTallyExportReport;
  dateTime: string;
}): Promise<{
  success: boolean;
  data?: SubTallyExportResponseBody;
  error?: any;
}> => {
  try {
    const exportResponse = await getSubTallyExportReport({
      sourceData: sourceData,
      sourceType: sourceType,
      filterTallySelectionsView: filterTallySelectionsView,
      selectSourceView: selectedSource[0].labels,
      actionType: 'EXPORTREPORT',
      reportType: subTallyResponse.reportType ?? '',
      tallyselecttype: tallySelectType,
      subtallyby: subTallyBy,
      dataAsOf: dateTime ? dateTime : '',
      isXaxisTallyBy: 1,
      subTallyResultFilePath: null,
    });
    return {success: true, data: exportResponse};
  } catch (error) {
    return {success: false, error: error};
  }
};

const cleanedPath = (path: string) => {
  return path;
};

export const cleanedIds = (ids: string[]) => {
  const cleanAlphas = ids.map((item) => item.replaceAll(/[A-Za-z]/g, ''));
  return cleanAlphas.join(',');
};

const sanitizedPayload = <T extends Record<string, any>>(data: T) => {
  return Object.fromEntries(
    Object.entries(data).map(([key, value]) => [key, value ?? ''])
  ) as T;
};

const transformFilters = (input: ActiveFilter[]) => {
  if (input.length == 0) {
    return [];
  }

  return input.map((item) => ({
    projectFieldId: item.id,
    projectFieldName: item.label,
    dataType: item.type,
    projectFieldLabel: item.label,
    type: item.type,
    operand:
      item.selectionType === 'include'
        ? 'IN'
        : item.selectionType === 'exclude'
          ? 'NOTIN'
          : item.selectionType,
    values: valueExtractors[item.id]?.(item) ?? [],
    metaDataID: 0,
    text: '',
  }));
};

const makeFilterValues = (item: any) => {
  switch (item.selectionType) {
    case 'include': {
      const list = item.values.map((v: any) => `N'${v.text}'`).join(',');
      return `IN(${list})`;
    }
    case 'exclude': {
      const list = item.values.map((v: any) => `N'${v.text}'`).join(',');
      return `NOT IN(${list})`;
    }
    case 'between':
      if (item.type == 'DATETIME') {
        return `BETWEEN CAST('${item.startDate}' AS ${item.type}) AND CAST('${item.endDate}' AS ${item.type})`;
      } else return `BETWEEN('${item.startSize}' AND '${item.endSize}')`;
    case 'after':
      return `GREATERTHAN CAST('${item.endDate == '' ? item.startDate : item.endDate}' AS ${item.type})`;
    case 'over':
      return `GREATERTHAN('${item.endSize == '' ? item.startSize : item.endSize}')`;
    case 'before':
      return `LESSTHAN CAST('${item.endDate == '' ? item.startDate : item.endDate}' AS ${item.type})`;
    case 'below':
      return `LESSTHAN('${item.endSize == '' ? item.startSize : item.endSize}')`;
    case 'on':
      return `= CAST('${item.endDate == '' ? item.startDate : item.endDate}' AS ${item.type})`;
    case 'at':
      return `=('${item.endSize == '' ? item.startSize : item.endSize}')`;
    default:
      return `=(${item.startDate ?? item.startSize})`;
  }
};

const transformMDFilters = (input: ActiveFilter[]) => {
  const arr = Object.values(input);

  if (!Array.isArray(arr) || arr.length === 0) return [];

  return arr.map((item: any) => {
    const opvalues = makeFilterValues(item);

    return {
      fieldID: item.id,
      fieldName: item.label,
      literalValue: opvalues,
      fieldType: 'metadata',
    };
  });
};

type ValueObj = {text: string};

const extractTexts = (item: any): string[] =>
  item.values?.map((v: ValueObj) => v.text) ?? [];

const compactRange = (start?: string, end?: string): string[] => {
  const out: string[] = [];
  if (start) out.push(start);
  if (end) out.push(end);
  return out;
};

const textValueIds = ['-57', '-64', '-5', '-15', '-7', '-19'];
const dateRangeIds = ['-82'];
const sizeRangeIds = ['-69'];

const valueExtractors: Record<string, (item: any) => string[]> = {};

for (const id of textValueIds) {
  valueExtractors[id] = extractTexts;
}

for (const id of dateRangeIds) {
  valueExtractors[id] = (item) => compactRange(item.startDate, item.endDate);
}

for (const id of sizeRangeIds) {
  valueExtractors[id] = (item) => compactRange(item.startSize, item.endSize);
}

const getNormalizedId = (n: any): string =>
  String(
    n?.id ??
      n?.keyID ??
      n?.value ??
      n?.securityGroupId ??
      n?.projectId ??
      n?.tagClassificationId ??
      ''
  );

const buildNestedTree = (flatList: any[] = []): any[] => {
  const nodeMap: Record<string, any> = {};
  const roots: any[] = [];
  (flatList ?? []).forEach((node) => {
    const id = getNormalizedId(node);
    const parentId = node?.parent != null ? String(node.parent) : '';
    nodeMap[id] = {...node, id, parentId, children: []};
  });
  Object.values(nodeMap).forEach((node: any) => {
    if (node.parentId && nodeMap[node.parentId]) {
      nodeMap[node.parentId].children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
};

const pickNodeLabel = (node: any) =>
  node?.textWithoutCount ??
  node?.text ??
  node?.securityGroupLabel ??
  node?.projectLabel ??
  node?.label ??
  node?.name ??
  node?.kayName ??
  '';

const mapToTreeItems = (nodes: any[] = []): any[] =>
  (nodes ?? []).map((n: any) => {
    const id = getNormalizedId(n);
    const label = pickNodeLabel(n);
    const children = Array.isArray(n?.children)
      ? mapToTreeItems(n.children)
      : undefined;
    const docCount =
      typeof n?.documentCount === 'number' && n.documentCount > 0
        ? n.documentCount
        : typeof n?.count === 'number' && n.count > 0
          ? n.count
          : undefined;
    return {
      id,
      label,
      fileType:
        children && children.length
          ? `${t('common.folder')}`
          : `${t('common.search')}`,
      disabled: !!n?.disabled,
      count: docCount,
      children,
    };
  });

export const buildTreeItems = (flatList: any[] = []): any[] => {
  const nestedTree = buildNestedTree(flatList);
  return mapToTreeItems(nestedTree);
};

export const mockData: TableRowData[] = [
  {
    tag: 'ATTORYNEY_CLIENT',
    count: '1',
    count1: '2',
    count2: '8',
    count3: '3',
    count4: '4',
    count5: '5',
    count6: '6',
    uniqueCount: '4',
    selected: {},
  },
  {
    tag: 'PRIVILEGED',
    count: '1',
    count1: '2',
    count3: '3',
    count2: '8',
    count4: '4',
    count5: '5',
    count6: '6',
    uniqueCount: '4',
    selected: {},
  },
  {
    tag: 'RESPONSIVE',
    count: '1',
    count1: '2',
    count2: '8',
    count3: '3',
    count4: '4',
    count5: '5',
    count6: '6',
    uniqueCount: '4',
    selected: {},
  },
  {
    tag: 'RESPONSE',
    count: '1',
    count1: '2',
    count3: '3',
    count4: '4',
    count5: '5',
    count2: '8',
    count6: '6',
    uniqueCount: '4',
    selected: {},
  },

  {
    tag: 'ATTORYNEY_CLIENT',
    count: '1',
    count1: '2',
    count2: '8',
    count3: '3',
    count4: '4',
    count5: '5',
    count6: '6',
    uniqueCount: '4',
    selected: {},
  },
  {
    tag: 'PRIVILEGED',
    count: '1',
    count1: '2',
    count3: '3',
    count2: '8',
    count4: '4',
    count5: '5',
    count6: '6',
    uniqueCount: '4',
    selected: {},
  },
  {
    tag: 'RESPONSIVE',
    count: '1',
    count1: '2',
    count2: '8',
    count3: '3',
    count4: '4',
    count5: '5',
    count6: '6',
    uniqueCount: '4',
    selected: {},
  },
];

export const prepareRows = (rows: any) => {
  return rows?.map((row: any, index: any) => {
    if (!('id' in row)) {
      return {...row, id: `row-${index + 1}`};
    }
    return row;
  });
};

export const columns = [
  {key: 'tag', label: 'Tags'},
  {key: 'count1', label: '2345675'},
  {key: 'count2', label: '23456789'},
  {key: 'count3', label: '23456778'},
  {key: 'count4', label: '2345600'},
  {key: 'count5', label: '23456787'},
  {key: 'count6', label: '234587437'},
  {key: 'uniqueCount', label: 'Unique Documents Count'},
];

export const getTallActionPAU = (
  t: TFunction<['translation', ...string[]], undefined>
) => [
  {id: '1', label: t('actionButton.menu.label_subTally'), value: 'Sub Tally'},
  {
    id: '2',
    label: t('actionButton.menu.label_view'),
    submenu: [
      {
        id: '1',
        label: t('actionButton.menu.label_ViewDoc'),
        value: t('actionButton.menu.label_ViewDoc'),
      },
      {
        id: '2',
        label: t('actionButton.menu.label_viewDoc_newtab'),
        value: t('actionButton.menu.label_viewDoc_newtab'),
      },
      {
        id: '3',
        label: t('actionButton.menu.labe_viewDocList'),
        value: t('actionButton.menu.labe_viewDocList'),
      },
    ],
  },
  {
    id: ReportActionMenuOptionTypes.BulkTag,
    label: t('actionButton.menu.label_bulkTag'),
    value: 'Bulk Tag',
  },
  {
    id: ReportActionMenuOptionTypes.BulkFolder,
    label: t('actionButton.menu.label_bulkFolder'),
    value: 'Bulk Folder',
  },
  {
    id: ReportActionMenuOptionTypes.BulkRelease,
    label: t('actionButton.menu.label_bulkRelease'),
    value: 'Bulk Release',
  },
  {
    id: '6',
    label: t('actionButton.menu.label_ExportData'),
    value: 'Export Data',
  },
];

export const getTallyActionRMU = (
  t: TFunction<['translation', ...string[]], undefined>
) => [
  {id: '1', label: t('actionButton.menu.label_subTally'), value: 'Sub Tally'},
  {
    id: '2',
    label: t('actionButton.menu.label_view'),
    submenu: [
      {
        id: '1',
        label: t('actionButton.menu.label_ViewDoc'),
        value: t('actionButton.menu.label_ViewDoc'),
      },
      {
        id: '2',
        label: t('actionButton.menu.label_viewDoc_newtab'),
        value: t('actionButton.menu.label_viewDoc_newtab'),
      },
      {
        id: '3',
        label: t('actionButton.menu.labe_viewDocList'),
        value: t('actionButton.menu.labe_viewDocList'),
      },
    ],
  },
  {
    id: ReportActionMenuOptionTypes.BulkTag,
    label: t('actionButton.menu.label_bulkTag'),
    value: 'Bulk Tag',
  },
  {
    id: ReportActionMenuOptionTypes.BulkFolder,
    label: t('actionButton.menu.label_bulkFolder'),
    value: 'Bulk Folder',
  },
  {
    id: ReportActionMenuOptionTypes.BulkAssign,
    label: t('actionButton.menu.label_bulkAssign'),
    value: 'bulkAssign',
  },
  {
    id: ReportActionMenuOptionTypes.QuickBatch,
    label: t('actionButton.menu.label_quickBatch'),
    value: 'Quick Batch',
  },
  {
    id: '7',
    label: t('actionButton.menu.label_ExportData'),
    value: 'Export Data',
  },
];

export const getSubtallyActionPAU = (
  t: TFunction<['translation', ...string[]], undefined>
) => [
  {
    id: '1',
    label: t('actionButton.menu.label_view'),
    submenu: [
      {
        id: '1',
        label: t('actionButton.menu.label_ViewDoc'),
        value: t('actionButton.menu.label_ViewDoc'),
      },
      {
        id: '2',
        label: t('actionButton.menu.label_viewDoc_newtab'),
        value: t('actionButton.menu.label_viewDoc_newtab'),
      },
      {
        id: '3',
        label: t('actionButton.menu.labe_viewDocList'),
        value: t('actionButton.menu.labe_viewDocList'),
      },
    ],
  },
  {
    id: ReportActionMenuOptionTypes.BulkTag,
    label: t('actionButton.menu.label_bulkTag'),
    value: 'Bulk Tag',
  },
  {
    id: ReportActionMenuOptionTypes.BulkFolder,
    label: t('actionButton.menu.label_bulkFolder'),
    value: 'Bulk Folder',
  },
  {
    id: ReportActionMenuOptionTypes.BulkRelease,
    label: t('actionButton.menu.label_bulkRelease'),
    value: 'Bulk Release',
  },
  {
    id: '5',
    label: t('actionButton.menu.label_ExportData'),
    value: 'Export Data',
  },
];

export const getSubtallyActionRMU = (
  t: TFunction<['translation', ...string[]], undefined>
) => [
  {
    id: '1',
    label: t('actionButton.menu.label_view'),
    submenu: [
      {
        id: '1',
        label: t('actionButton.menu.label_ViewDoc'),
        value: t('actionButton.menu.label_ViewDoc'),
      },
      {
        id: '2',
        label: t('actionButton.menu.label_viewDoc_newtab'),
        value: t('actionButton.menu.label_viewDoc_newtab'),
      },
      {
        id: '3',
        label: t('actionButton.menu.labe_viewDocList'),
        value: t('actionButton.menu.labe_viewDocList'),
      },
    ],
  },
  {
    id: ReportActionMenuOptionTypes.BulkTag,
    label: t('actionButton.menu.label_bulkTag'),
    value: 'Bulk Tag',
  },
  {
    id: ReportActionMenuOptionTypes.BulkFolder,
    label: t('actionButton.menu.label_bulkFolder'),
    value: 'Bulk Folder',
  },
  {
    id: ReportActionMenuOptionTypes.BulkAssign,
    label: t('actionButton.menu.label_bulkAssign'),
    value: 'Bulk Assign',
  },
  {
    id: ReportActionMenuOptionTypes.QuickBatch,
    label: t('actionButton.menu.label_quickBatch'),
    value: 'Quick Batch',
  },
  {
    id: '6',
    label: t('actionButton.menu.label_ExportData'),
    value: 'Export Data',
  },
];

export const roles = {
  systemAdmin: 'System Administrator',
  domainAdmin: 'Domain Administrator',
  reviewManager: 'Review Manager',
  reviewer: 'Reviewer',
  projectAdmin: 'Project Administrator',
};

export const STR_Action_Menu_List: DocActionMenuItem[] = [
  {
    id: 'View',
    name: 'View',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
    subItems: [
      {
        id: 'ViewInDocView',
        name: 'ViewInDocView',
        Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
      },
      {
        id: 'ViewInDocViewInNewTab',
        name: 'ViewInDocViewInNewTab',
        Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
      },
      {
        id: 'ViewInDocList',
        name: 'ViewInDocList',
        Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
      },
    ],
  },
  {
    id: 'BulkTag',
    name: 'BulkTag',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
  {
    id: 'BulkFolder',
    name: 'BulkFolder',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
  {
    id: 'BulkAssign',
    name: 'BulkAssign',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'QuickBatch',
    name: 'QuickBatch',
    Roles: [RoleId.ReviewManager],
  },
  {
    id: 'BulkRelease',
    name: 'BulkRelease',
    Roles: [RoleId.ProjectAdministrator],
  },
  {
    id: 'ExportData',
    name: 'ExportData',
    Roles: [RoleId.ProjectAdministrator, RoleId.ReviewManager],
  },
];

export type Id = string | number | undefined;

export const hasRequiredIds = (notificationId: Id, reportId: Id): boolean => {
  const ok = (v: Id) => v !== undefined && String(v).trim() !== '';
  return ok(notificationId) && ok(reportId);
};

export const handleReportDocId = async ({
  sourceType,
  actionConfig,
  requiredData,
  getReportDocId,
}: {
  sourceType: any;
  actionConfig: any;
  requiredData: any;
  getReportDocId: GetReportDocId;
}): Promise<{
  success: boolean;
  data?: IGetReportDocIdResponse;
}> => {
  const fieldTypeForSubTally =
    actionConfig.destinationType === 'DOCLIST'
      ? requiredData?.SubtallyData?.type
      : '';
  try {
    const resultResponse = await getReportDocId({
      tallySelection: requiredData?.TallyResults?.docs.map(
        (doc: any) => doc.label
      ) ?? [''],
      tallyByText: actionConfig?.tallyByText,
      subtallySelection:
        sourceType == 'SUBTALLY'
          ? requiredData?.SubtallyResults?.docs.map((item: any) => [
              item.label,
              item.header,
            ])
          : [],
      isTallyBulk: true,
      fieldType:
        sourceType == 'TALLY'
          ? requiredData?.TallyData?.type
          : fieldTypeForSubTally,
      selectedIdForActions:
        sourceType == 'TALLY'
          ? (getResultLabelIds(
              requiredData?.TallyData?.idLabels,
              requiredData?.TallyResults?.docs
            ) ?? '')
          : '',
      destinationContextTypeCode: actionConfig?.destinationType,
      sourceTypeCode: sourceType,
      actionName: actionConfig?.actionName,
      isSubTally: sourceType == 'SUBTALLY',
      isDocumentCount: actionConfig.isDocumentCount,
      docViewSessionObject: {
        sortconfiguration: {
          optimizedCols: null,
          optimizedSelectFieldIDs: null,
          optimizedSelectFieldOrder: null,
          manualCols: null,
          manualSelectFieldIDs: null,
          manualSelectFieldOrder: null,
          selectFields: null,
          configuredCols: null,
          order: null,
          showCompletedDocOptimized: false,
          showCompletedDocManual: false,
          configuredShowCompletedDoc: false,
          selectFieldTemp: null,
          selectFieldDataTypeOptimized: null,
          selectFieldDataType: null,
          selectFieldDataTypeManual: null,
          redirectionType: 0,
          manualOrder: 'null',
        },
        assignmentSortDetails: [],
        documentListMasterID: 0,
        sourceTypeCode: null,
        sourceID: 0,
        assignmentSource: null,
        xmlCompletedDocumentEditedCodingForm: null,
        userCodingFormID: 0,
        docsForCodeSame: [],
        docIdsForDocHistory: [],
        backtoSrcDoclistMasterId: 0,
        backtoSrcSourceId: 0,
        backtoSrcSourceTypeCode: null,
      },
      tallyFilter: {
        sourceType: requiredData?.SourceData[0]?.sourceName,
        sourceID: requiredData?.SourceData[0]?.source,
        docListID: 0,
        tally: {
          tallyByID: getMetaId(requiredData?.TallyData),
          tallyBy: null,
          docListID: 0,
          tallyByCriteriaList: requiredData?.TallyResults?.list ?? [],
          filterByMetaData: [],
          filterBySelection: [],
          isBulkAction: false,
          tallyByType: requiredData?.TallyData?.type ?? null,
          tallyByIDExceptMetadata: getNonMetaIds(requiredData?.TallyData) ?? '',
          subTallyByID: null,
          subTallyByIDExceptMetadata: null,
          subTallyByType: null,
          tallyResultIDExceptMetadata:
            getResultLabelIds(
              requiredData?.TallyData?.idLabels,
              requiredData?.TallyResults?.docs
            ) ?? '',
          tallyResultID: null,
          subTallyByResultIDExceptMetadata: null,
          tallyFilterValue:
            requiredData?.TallyData?.filters.length > 0
              ? requiredData?.TallyData?.filters
              : null,
          subTallyFilterValue: [],
        },
        subTally: {
          tallyByID:
            sourceType == 'SUBTALLY'
              ? getMetaId(requiredData?.SubtallyData)
              : '',
          tallyBy:
            sourceType == 'SUBTALLY'
              ? getSelectedLabels(requiredData.SubtallyData.idLabels)
              : null,
          docListID: 0,
          tallyByCriteriaList: requiredData?.SubtallyResults?.list ?? [],
          filterByMetaData: [],
          filterBySelection: [],
          isBulkAction: false,
          tallyByType:
            sourceType == 'SUBTALLY' ? requiredData?.SubtallyData?.type : null,
          tallyByIDExceptMetadata:
            sourceType == 'SUBTALLY'
              ? (getNonMetaIds(requiredData?.TallyData) ?? '')
              : null,
          subTallyByID: null,
          subTallyByIDExceptMetadata:
            sourceType == 'SUBTALLY'
              ? (getNonMetaIds(requiredData?.SubtallyData) ?? '')
              : null,
          subTallyByType: null,
          tallyResultIDExceptMetadata:
            sourceType == 'SUBTALLY'
              ? (getResultLabelIds(
                  requiredData?.SubtallyData?.idLabels,
                  requiredData?.SubtallyResults?.docs
                ) ?? '')
              : null,
          tallyResultID: null,
          subTallyByResultIDExceptMetadata: null,
          tallyFilterValue:
            requiredData?.SubtallyData?.filters.length > 0
              ? requiredData?.TallyDSubtallyDataata?.filters
              : null,
          subTallyFilterValue: [],
        },
        dataAsOf:
          sourceType == 'TALLY'
            ? requiredData?.TallyResults?.dataasof
            : requiredData?.SubtallyResults?.dataasof,
        isDocumentCount: false,
        documentCount: 0,
        navigationSourceType:
          requiredData?.SourceData[0]?.navigationSourceType || '',
        reportFilePath:
          sourceType == 'TALLY'
            ? requiredData?.TallyResponse?.reportPath
            : requiredData?.SubtallyResponse?.reportPath,
        reportName:
          sourceType == 'TALLY'
            ? requiredData?.TallyResponse?.reportName
            : requiredData?.SubtallyResponse?.reportName,
      },
      tallyFilterSession: null,
      tallyInput: {
        lstSecurityGroups: [],
        lstProjects: [],
        lstMetadata: [],
        lstProductionMetadata: [],
        lstDocuments: [],
        searchTreeList: [],
        assignmentTreeList: [],
        folderTreeList: [],
        lstPopupSqlMetaData: [],
        lstFilterSqlMetaData: [],
        lstFileSizeType: [
          {
            fileSizeTypeCode: 'BYTES',
            fileSizeTypeLabel: 'Bytes',
            isDefault: false,
            divisorFromBytes: 1,
          },
          {
            fileSizeTypeCode: 'KB',
            fileSizeTypeLabel: 'KB',
            isDefault: false,
            divisorFromBytes: 1024,
          },
          {
            fileSizeTypeCode: 'GB',
            fileSizeTypeLabel: 'Gigabyte',
            isDefault: false,
            divisorFromBytes: 1073741824,
          },
          {
            fileSizeTypeCode: 'MB',
            fileSizeTypeLabel: 'MB',
            isDefault: true,
            divisorFromBytes: 1048576,
          },
          {
            fileSizeTypeCode: 'TB',
            fileSizeTypeLabel: 'Terabyte',
            isDefault: true,
            divisorFromBytes: 1099511627776,
          },
        ],
        lstTagsList: [],
        lstRedactionTagsList: [],
        lstTagClassification: [],
        aiScoreTreeList: null,
        xaxis: null,
        yaxis: null,
        isDocCountView: true,
        divisorFromBytes: 1048576,
      },
    });
    return {success: true, data: resultResponse};
  } catch {
    return {success: false};
  }
};
export const handleExportView = async ({
  docId,
  getExportView,
}: {
  docId: number;
  getExportView: GetExportView;
}): Promise<{
  success: boolean;
  data?: IGetExportViewResponse;
}> => {
  try {
    const resultResponse = await getExportView({
      reportID: null,
      share: '',
      reportLabel: '',
      tempDataRedirectFrom: 'TALLY',
      tempDataSourceList: [[`${docId}`]],
      tempDataSourceName: 'DOCLIST',
      isPopup: false,
    });
    return {success: true, data: resultResponse};
  } catch {
    return {success: false};
  }
};

export const handleDocReport = async ({
  sourceType,
  docListId,
  totalDocumentToView,
  actionConfig,
  requiredData,
  getDocReport,
}: {
  sourceType: any;
  docListId: any;
  totalDocumentToView: any;
  actionConfig: any;
  requiredData: any;
  getDocReport: GetDocReport;
}): Promise<{
  success: boolean;
  data?: IGetDocReportResponse;
}> => {
  const fieldTypeForSubTally =
    actionConfig.destinationType === 'DOCLIST'
      ? requiredData?.SubtallyData?.type
      : '';
  try {
    const resultResponse = await getDocReport({
      tallySelection:
        requiredData?.TallyResults?.docs.map((doc: any) => doc.label) ?? [],
      tallyByText: actionConfig?.tallyByText,
      subtallySelection:
        sourceType == 'SUBTALLY'
          ? requiredData?.SubtallyResults?.docs.map((item: any) => [
              item.label,
              item.header,
            ])
          : [],
      isTallyBulk: true,
      fieldType:
        sourceType == 'TALLY'
          ? requiredData?.TallyData?.type
          : fieldTypeForSubTally,
      selectedIdForActions:
        sourceType == 'TALLY'
          ? (getResultLabelIds(
              requiredData?.TallyData?.idLabels,
              requiredData?.TallyResults?.docs
            ) ?? '')
          : '',
      destinationContextTypeCode: actionConfig?.destinationType,
      sourceTypeCode: sourceType,
      actionName: actionConfig?.actionName,
      isSubTally: sourceType == 'SUBTALLY',
      isDocumentCount: false,
      totalDocumentToView: totalDocumentToView,
      docViewSessionObject: null,
      tallyFilter: {
        sourceType: requiredData?.SourceData[0]?.sourceName,
        sourceID: requiredData?.SourceData[0]?.source,
        docListID: docListId,
        tally: {
          tallyByID: getMetaId(requiredData?.TallyData),
          tallyBy: null,
          docListID: 0,
          tallyByCriteriaList: requiredData?.TallyResults?.list ?? [],
          filterByMetaData: [],
          filterBySelection: [],
          isBulkAction: false,
          tallyByType: requiredData?.TallyData?.type ?? null,
          tallyByIDExceptMetadata: getNonMetaIds(requiredData?.TallyData) ?? '',
          subTallyByID: null,
          subTallyByIDExceptMetadata: null,
          subTallyByType: null,
          tallyResultIDExceptMetadata:
            getResultLabelIds(
              requiredData?.TallyData?.idLabels,
              requiredData?.TallyResults?.docs
            ) ?? '',
          tallyResultID: null,
          subTallyByResultIDExceptMetadata: null,
          tallyFilterValue: [requiredData?.TallyData?.filters],
          subTallyFilterValue: [],
        },
        subTally: {
          tallyByID:
            sourceType == 'SUBTALLY'
              ? getMetaId(requiredData?.SubtallyData)
              : '',
          tallyBy:
            sourceType == 'SUBTALLY'
              ? getSelectedLabels(requiredData.SubtallyData.idLabels)
              : null,
          docListID: 0,
          tallyByCriteriaList: requiredData?.SubtallyResults?.list ?? [],
          filterByMetaData: [],
          filterBySelection: [],
          isBulkAction: false,
          tallyByType:
            sourceType == 'SUBTALLY' ? requiredData?.SubtallyData?.type : null,
          tallyByIDExceptMetadata: null,
          subTallyByID: null,
          subTallyByIDExceptMetadata:
            sourceType == 'SUBTALLY'
              ? (getNonMetaIds(requiredData?.SubtallyData) ?? '')
              : null,
          subTallyByType: null,
          tallyResultIDExceptMetadata:
            sourceType == 'SUBTALLY'
              ? (getResultLabelIds(
                  requiredData?.SubtallyData?.idLabels,
                  requiredData?.SubtallyResults?.docs
                ) ?? '')
              : null,
          tallyResultID: null,
          subTallyByResultIDExceptMetadata:
            sourceType == 'SUBTALLY'
              ? (getNonMetaIds(requiredData?.TallyData) ?? '')
              : null,
          tallyFilterValue: [requiredData?.SubtallyData?.filters],
          subTallyFilterValue: [],
        },

        navigationSourceType:
          requiredData?.SourceData[0]?.navigationSourceType || '',
      },
      tallyFilterSession: null,
      tallyDTO: {
        lstSecurityGroups: [],
        lstProjects: [],
        lstMetadata: [],
        lstProductionMetadata: [],
        lstDocuments: [],
        searchTreeList: [],
        assignmentTreeList: [],
        folderTreeList: [],
        lstPopupSqlMetaData: [],
        lstFilterSqlMetaData: [],
        lstFileSizeType: [],
        lstTagsList: [],
        lstRedactionTagsList: [],
        lstTagClassification: [],
        aiScoreTreeList: null,
        xaxis: null,
        yaxis: null,
        isDocCountView: true,
        divisorFromBytes: 0,
      },
    });
    return {success: true, data: resultResponse};
  } catch {
    return {success: false};
  }
};

export const convertToIsoFormat = (input: string) => {
  const date = new Date(input);
  if (isNaN(date.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    date.getUTCFullYear() +
    '-' +
    pad(date.getUTCMonth() + 1) +
    '-' +
    pad(date.getUTCDate()) +
    'T' +
    pad(date.getUTCHours()) +
    ':' +
    pad(date.getUTCMinutes()) +
    ':' +
    pad(date.getUTCSeconds())
  );
};

export const getMetaId = (data: any) =>
  data.type === 'Metadata' ? data.id : null;

export const getNonMetaIds = (data: any) =>
  data.type === 'Metadata' ? null : data.id;

export const getResultLabelIds = (idLabels: any, docs: any) => {
  const labelToId = new Map<string, string>();
  for (const {Id, Label} of idLabels) {
    if (!Label) continue;
    labelToId.set(Label.trim().toLowerCase(), Id);
  }
  const ids: string[] = [];
  for (const {label} of docs) {
    if (!label) continue;
    const key = label.trim().toLowerCase();
    const found = labelToId.get(key);
    if (found) ids.push(found);
  }
  const uniqueIds = Array.from(new Set(ids));

  return uniqueIds.join(',');
};

export const getReportIdFromUrl = (
  queryParams: string
): number | null | undefined => {
  const params = new URLSearchParams(queryParams);
  const idParam = params.get('ReportID') ?? '';
  const reportId = Number(idParam);
  return Number.isFinite(reportId) ? reportId : null;
};

export const getReportNameFromUrl = (
  queryParams: string
): string | null | undefined => {
  const params = new URLSearchParams(queryParams);
  return params.get('ReportLabel');
};
export const resolveIdFromFixedData = (
  fixedData: any,
  input: any,
  validate = true
) => {
  if (!Array.isArray(fixedData)) return null;
  if (!input || !input.key) return null;

  const keyStr = String(input.key).trim();

  const parent = fixedData.find(
    (block) =>
      block?.id &&
      String(block.id).trim().toLowerCase() === keyStr.toLowerCase()
  );
  if (!parent) {
    throw new Error(`Parent block "${keyStr}" not found in fixedData`);
  }

  const keyArr = Array.isArray(parent.key) ? parent.key : [];
  if (!keyArr.length) {
    throw new Error(`Parent block "${keyStr}" has no key[] prefix`);
  }
  const prefix = String(keyArr[0]).trim();
  if (!prefix) {
    throw new Error(`Invalid prefix in parent block "${keyStr}"`);
  }

  const idTokens = String(input.id)
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);

  const parseNum = (s: any) => {
    const m = s.match(/-?\d+/);
    if (!m) throw new Error(`Could not parse numeric id from: ${s}`);
    return parseInt(m[0], 10);
  };

  const formatted = idTokens.map((tok) => {
    const n = parseNum(tok);
    return n < 0 ? `${prefix}-${Math.abs(n)}` : `${prefix}${n}`;
  });

  if (!validate) {
    return {prefix, formatted};
  }

  const dashedToCheck = new Set(
    idTokens.map((tok) => {
      const n = parseNum(tok);
      return `${prefix}-${Math.abs(n)}`;
    })
  );

  const existsMap = Object.create(null);
  for (const dashed of dashedToCheck) existsMap[dashed] = false;

  const stack = Array.isArray(parent.data) ? [...parent.data] : [];
  while (stack.length) {
    const node = stack.pop();
    if (node && typeof node === 'object') {
      if (node.id && dashedToCheck.has(node.id)) {
        existsMap[node.id] = true;
      }
      if (Array.isArray(node.children)) {
        stack.push(...node.children);
      }
    }
  }

  return {prefix, formatted, existenceMap: existsMap};
};

export const filterSelectionData = (
  selectionData: SelectionItem[],
  selectedIds: string[]
) => {
  const ids = selectedIds;
  if (!Array.isArray(selectionData)) return;
  const filterChildren = (items: any[]): any[] =>
    items
      .map((item) => ({
        ...item,
        children: item.children ? filterChildren(item.children) : undefined,
      }))
      .filter((item) => {
        const isSelected = ids.includes(item.id);
        const hasChildren = item.children?.length > 0;
        return !isSelected && (hasChildren || !item.children);
      });
  const filteredData = selectionData.map((section) => {
    const newSection = {
      ...section,
      data: section.data ? filterChildren(section.data) : [],
    };
    newSection.disabled = newSection.data.length === 0;
    return newSection;
  });
  return filteredData;
};

// Export field types the uspReportExportData stored procedure matches on, where they cannot be derived from the work product label.
const WORK_PRODUCT_EXPORT_TYPE: Record<string, string> = {
  'Redaction Tags': 'Redaction',
  Assignments: 'Assign',
  Scores: 'AIScore',
};

export const singularizeWorkProduct = (word: string) =>
  WORK_PRODUCT_EXPORT_TYPE[word] ??
  (word.endsWith('s') ? word.slice(0, -1) : word);

export const getSelectedLabels = (
  idLabels: {Id: string; Label: string}[]
): string => {
  const result = idLabels.map((item) => item.Label).join(',');
  return result;
};
